import { type NiceModalHandler, useModal } from '@ebay/nice-modal-react'
import { styled } from '@linaria/react'
import { Link } from '@tanstack/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useDimensions from '../../hooks/useDimensions'
import type { ApiResponse } from '../../types/api'
import type { WithdrawHistoryObj } from '../../types/wallet-management'
import { api } from '../../utils/api-client'
import classNames from '../../utils/classNames'
import closeModal from '../../utils/closeModal'
import { numberWithCommas } from '../../utils/number'
import MessageModal from '../common/message-modal'
import Spinner from '../common/spinner'
import Icon from '../icon'
import CancelTransactionModal from './cancel-transaction-modal'

const Header = styled.div`
  padding: 0 16px;
  .theme-dark & {
    border-bottom: 1px solid #1f223c;
  }
  .theme-light & {
    border-bottom: 1px solid #fff;
  }
`

const Text = styled.p`
  font-size: 12px;
  line-height: 14px;
  font-family: 'Avenir-Light';
  color: var(--txt);
  &.title {
    font-size: 14px;
  }
  &.bold {
    font-family: 'Avenir-Heavy';
  }
`

const Refresh = styled.button`
  display: flex;
  padding: 12px 16px;
  margin-right: -16px;
  svg {
    fill: var(--txt);
  }
  @keyframes rotate {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  &.rotate {
    animation: rotate 1.5s linear infinite;
  }
`

const ScrollList = styled.div`
  padding: 0 16px;
  max-height: calc(100vh - 144px);
  overflow-y: auto;
  @media (min-width: 1200px) {
    max-height: calc(100vh - 450px);
    /* width */
    &::-webkit-scrollbar {
      width: 7px;
    }

    /* Track */
    &::-webkit-scrollbar-track {
      background: transparent;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      border: 5px solid transparent;
      border-radius: 9px;
      background-clip: content-box;
    }

    .theme-dark &::-webkit-scrollbar-thumb {
      background-color: #000;
    }

    .theme-light &::-webkit-scrollbar-thumb {
      background-color: #ccc;
    }
  }
`

const HistoryItem = styled.div`
  padding: 12px 0;
  gap: 6px;
  .theme-dark & {
    border-bottom: 1px solid #1f223c;
  }
  .theme-light & {
    border-bottom: 1px solid #fff;
  }
`

const Status = styled.p`
  font-size: 12px;
  font-family: 'Avenir-Light';
  font-style: italic;
  color: #878787;
  text-align: right;
  &.success {
    color: #9bdf45;
  }
  &.failed {
    color: #ff4646;
  }
  &.pending {
    color: #23a8f7;
  }
`

const CheckMore = styled.span`
  font-size: 14px;
  font-family: 'Avenir-Light';
  color: #df0033;
  text-align: center;
  padding: 12px 0;
  text-decoration: underline;
  display: block;
`

const Cancel = styled.button`
  font-size: 12px;
  font-family: 'Avenir-Light';
  color: #202632;
  padding: 3px 5px;
  background-color: #fff;
  margin-right: 8px;
`

const Empty = styled.div`
  height: 120px;
  p {
    font-size: 12px;
    color: #9a9a9a;
    text-align: center;
    font-family: 'Avenir-Light';
  }
  @media (min-width: 1200px) {
    p {
      font-size: 16px;
    }
  }
`

type WithdrawHistoryProps = {
  closeWalletManagementModal?: () => void
  from?: 'profile' | 'withdraw'
}

const WithdrawHistory = ({
  closeWalletManagementModal,
  from = 'profile',
}: WithdrawHistoryProps) => {
  const cancelTransactionModal = useModal(CancelTransactionModal)
  const messageModal = useModal(MessageModal)
  const { t } = useTranslation(['common', 'cashier', 'walletManagement'])
  const { isDesktop } = useDimensions()
  const [withdrawList, setWithdrawList] = useState<WithdrawHistoryObj[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [cancelLoading, setCancelLoading] = useState(false)

  const fetchList = async () => {
    const reqBody = {
      type: 'record_list',
      record_type: 'debit',
    }
    setIsLoading(true)
    try {
      const response = await api.post<ApiResponse<WithdrawHistoryObj[]>>(
        '/ajax_data.php',
        reqBody,
      )
      const { status, info } = response.data
      if (status === 1) {
        setWithdrawList(info)
      } else {
        setWithdrawList([])
      }
      setIsLoading(false)
    } catch (error) {
      console.error(error)
      setWithdrawList([])
      setIsLoading(false)
    }
    setIsFirstLoad(false)
  }

  useEffect(() => {
    fetchList()
  }, [])

  const cancelDebit = async (selectedTransaction: string | null) => {
    const reqBody = {
      type: 'cancel_debit',
      id: selectedTransaction,
    }
    setCancelLoading(true)

    try {
      const response = await api.post<ApiResponse<string>>(
        '/ajax_data.php',
        reqBody,
      )
      const { status, info } = response.data
      if (status === 1) {
        fetchList()
      }
      messageModal.show({
        title: '',
        message: info,
      })
    } catch (error) {
      console.error(error)
    }

    closeModal(cancelTransactionModal as NiceModalHandler)
    setCancelLoading(false)
  }

  const getStatusClassname = useCallback((verifyStatus: number): string => {
    if ([1, 3].includes(verifyStatus)) return 'success'
    else if (verifyStatus === 2) return 'failed'
    else if (verifyStatus === 5) return 'pending'
    return ''
  }, [])

  const content = useMemo(() => {
    if (isLoading && isFirstLoad)
      return <Spinner containerStyle={{ height: '200px' }} />
    if (withdrawList.length === 0)
      return (
        <Empty className="flx-ctr">
          <p>{t('no-transactions', { ns: 'cashier' })}</p>
        </Empty>
      )
    return (
      <>
        {withdrawList.map(item => (
          <HistoryItem key={item.id} className="flx-btw-ctr">
            <div className="flex-1">
              <Text style={{ marginBottom: '3px' }}>{item.cardNumber}</Text>
              <Text>{item.requestTime}</Text>
            </div>
            {item.verifyStatus === 2 && (
              <Text className="flex-1 text-center">
                {item.verifyComment
                  ? item.verifyComment
                  : t('contact-support', { ns: 'common' })}
              </Text>
            )}
            <div
              className="flex justify-end items-center"
              style={{ flex: 0.7 }}>
              {[0, 5, 7].includes(item.verifyStatus) && (
                <Cancel
                  onClick={() => {
                    cancelTransactionModal.show({
                      isLoading: cancelLoading,
                      onClick: () => cancelDebit(item.id),
                    })
                  }}>
                  {t('cancel', { ns: 'common' })}
                </Cancel>
              )}
              <div>
                <Text
                  className="bold"
                  style={{ marginBottom: '3px', textAlign: 'right' }}>
                  $ {numberWithCommas(parseFloat(item.amount))}
                </Text>
                {item.verifyStatus !== 5 && (
                  <Status className={getStatusClassname(item.verifyStatus)}>
                    {item.status}
                  </Status>
                )}
              </div>
            </div>
          </HistoryItem>
        ))}
        <Link
          to="/profile/financial-transaction"
          search={{ tab: 'withdrawals' }}>
          <CheckMore
            onClick={() =>
              isDesktop &&
              closeWalletManagementModal &&
              closeWalletManagementModal()
            }>
            {t('check-more', { ns: 'walletManagement' })}
          </CheckMore>
        </Link>
      </>
    )
  }, [isLoading, withdrawList])

  return (
    <div className={classNames('rounded', from === 'profile' && 'bg-tertiary')}>
      <Header className="flx-btw-ctr">
        <Text className="bold title">
          {t('last-5-transactions', {
            ns: 'walletManagement',
          })}
        </Text>
        <Refresh
          className={isLoading && !isFirstLoad ? 'rotate' : ''}
          onClick={fetchList}>
          <Icon id="refresh" width="16" height="16" />
        </Refresh>
      </Header>
      <ScrollList>{content}</ScrollList>
    </div>
  )
}

export default WithdrawHistory
